// src/components/ProfileForm/ProfileForm.js

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import ProfileImageUpload from './ProfileImageUpload';
import Button from '../Button';
import { useFocusTrap } from '../../hooks';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { validateUsername } from '../../utils/validation';
import { mergeProfileDefaults } from '../../utils/profile';
import styles from './ProfileForm.module.css';

const touchedFromErrors = (errors) =>
  Object.keys(errors).reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {});

const ProfileForm = ({
  initialValues,
  mode = 'edit',
  onSubmit,
  onCancel,
  pending = false,
  submitErrorKey,
}) => {
  const { t } = useTranslation();
  const defaults = useMemo(() => mergeProfileDefaults(initialValues), [initialValues]);

  const [values, setValues] = useState(defaults);
  const [originalUsername, setOriginalUsername] = useState(defaults.username);
  const [profilePicture, setProfilePicture] = useState(null);
  const [touched, setTouched] = useState({});
  const [generalErrorKey, setGeneralErrorKey] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  useEffect(() => {
    setValues(defaults);
    setOriginalUsername(defaults.username);
    setProfilePicture(null);
    setTouched({});
    setGeneralErrorKey(null);
  }, [defaults]);

  useEffect(() => {
    setGeneralErrorKey(submitErrorKey || null);
  }, [submitErrorKey]);

  const dialogRef = useFocusTrap(true, {
    initialFocus: true,
    restoreFocus: true,
    initialFocusDelay: 150,
  });

  useBodyScrollLock(true);

  useEffect(() => {
    if (!onCancel || mode === 'create') return;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (pending || isCropModalOpen || event.defaultPrevented) return;
      onCancel();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, pending, isCropModalOpen, mode]);

  const validate = (currentValues) => {
    const errors = {};
    if (!currentValues.displayName?.trim()) {
      errors.displayName = 'profile.display_name_required';
    }

    const usernameError = validateUsername(currentValues.username);
    if (usernameError) {
      errors.username = usernameError;
    }

    return errors;
  };

  const validationErrors = validate(values);
  const hasErrors = Object.keys(validationErrors).length > 0;
  const isSubmitDisabled = hasErrors || !onSubmit;

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setValues((prev) => ({
      ...prev,
      [field]: field === 'username' ? value.toLowerCase() : value,
    }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleImageSelect = (file) => {
    setProfilePicture(file);
  };

  const handleSubmit = async (event) => {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    if (pending) return;

    const errors = validate(values);
    if (Object.keys(errors).length > 0) {
      setTouched((prev) => ({ ...prev, ...touchedFromErrors(errors) }));
      setGeneralErrorKey(null);
      return;
    }

    if (!onSubmit) return;

    setGeneralErrorKey(null);

    try {
      await onSubmit(values, originalUsername, profilePicture);
    } catch (error) {
      const key =
        error?.message === 'Username already taken'
          ? 'profile.username_taken'
          : 'profile.update_failed';
      setGeneralErrorKey(key);
    }
  };

  const isCreateMode = mode === 'create';
  const titleKey = isCreateMode ? 'profile.create_profile' : 'profile.edit_profile';
  const titleText = t(
    titleKey,
    isCreateMode ? 'Complete your profile' : 'Edit profile'
  );

  const displayNameError = touched.displayName && validationErrors.displayName;
  const usernameError = touched.username && validationErrors.username;

  const handleOverlayClick = () => {
    if (!onCancel || pending || mode === 'create' || isCropModalOpen) return;
    onCancel();
  };

  const modalRoot =
    typeof document !== 'undefined'
      ? document.getElementById('modal-root') || document.body
      : null;

  const modalContent = (
    <div className={styles.profileFormOverlay} onClick={handleOverlayClick}>
      <form
        className={styles.profileFormDialog}
        ref={dialogRef}
        role="dialog"
        aria-modal={isCropModalOpen ? undefined : 'true'}
        aria-labelledby="profile-form-title"
        aria-hidden={isCropModalOpen ? 'true' : undefined}
        aria-busy={pending}
        inert={isCropModalOpen ? '' : undefined}
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <header className={styles.profileFormHeader}>
          <button
            type="button"
            onClick={onCancel}
            className={styles.profileFormCloseButton}
            aria-label={t('common.close')}
            disabled={pending || !onCancel}
          >
            <X size={20} />
          </button>
          <div className={styles.profileFormTitleGroup}>
            <h1 id="profile-form-title" className={styles.profileFormTitle}>
              {titleText}
            </h1>
          </div>
          <div className={styles.profileFormHeaderActions}>
            <Button
              variant="primary"
              size="small"
              type="submit"
              loading={pending}
              disabled={isSubmitDisabled}
            >
              {pending ? t('profile.saving', 'Saving...') : t('common.save', 'Save')}
            </Button>
          </div>
        </header>

        <div className={styles.profileFormBody}>
          <aside className={styles.profileFormSidebar}>
            <div className={styles.profileFormSidebarCard}>
              <ProfileImageUpload
                initialImage={values.profilePictureUrl}
                onImageSelect={handleImageSelect}
                onCropModalToggle={setIsCropModalOpen}
                disabled={pending}
              />
            </div>
          </aside>

          <div className={styles.profileFormMain}>
            <div className={styles.profileFormRow}>
              <div className={styles.formFieldGroup}>
                <div className={styles.formField}>
                  <label htmlFor="displayName">{t('profile.display_name')}</label>
                  <input
                    id="displayName"
                    type="text"
                    value={values.displayName}
                    onChange={handleChange('displayName')}
                    onBlur={() => handleBlur('displayName')}
                    placeholder={t('profile.enter_display_name')}
                    aria-invalid={Boolean(displayNameError)}
                    aria-describedby={displayNameError ? 'displayName-error' : undefined}
                    disabled={pending}
                  />
                </div>
                {displayNameError ? (
                  <p className={styles.formError} id="displayName-error">
                    {t(displayNameError)}
                  </p>
                ) : null}
              </div>

              <div className={styles.formFieldGroup}>
                <div className={styles.formField}>
                  <label htmlFor="username">{t('profile.username')}</label>
                  <input
                    id="username"
                    type="text"
                    value={values.username}
                    onChange={handleChange('username')}
                    onBlur={() => handleBlur('username')}
                    placeholder={t('profile.enter_username')}
                    aria-invalid={Boolean(usernameError)}
                    aria-describedby={usernameError ? 'username-error' : undefined}
                    disabled={pending}
                  />
                </div>
                {usernameError ? (
                  <p className={styles.formError} id="username-error">
                    {t(usernameError)}
                  </p>
                ) : null}
              </div>
            </div>

            <div className={styles.formFieldGroup}>
              <div className={styles.formField}>
                <label htmlFor="bio">{t('profile.bio')}</label>
                <textarea
                  id="bio"
                  value={values.bio}
                  onChange={handleChange('bio')}
                  placeholder={t('profile.enter_bio')}
                  rows={4}
                  disabled={pending}
                />
              </div>
            </div>

            {generalErrorKey ? (
              <div className={styles.formGeneralError} role="alert">
                {t(generalErrorKey)}
              </div>
            ) : null}
          </div>
        </div>

        <footer className={styles.profileFormFooter}>
          <Button
            variant="secondary"
            size="medium"
            onClick={onCancel}
            disabled={pending || !onCancel}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            size="medium"
            type="submit"
            loading={pending}
            disabled={isSubmitDisabled}
          >
            {pending ? t('profile.saving', 'Saving...') : t('profile.save_profile', 'Save profile')}
          </Button>
        </footer>
      </form>
    </div>
  );

  return modalRoot ? createPortal(modalContent, modalRoot) : modalContent;
};

export default ProfileForm;
